import React, { useState, useMemo, useEffect } from "react";
import {
  useGetPublicCommunityEventsQuery,
  useGetMyRSVPsQuery,
  useCreateRSVPMutation,
} from "../../services/api";
import {
  parseTimeTo24Hour,
  formatTimeForDisplay,
  generateGoogleCalendarUrl,
  calculateCountdown,
  formatEventDateTime,
  getEventStatus,
  getEventInstant,
} from "../../utils/eventUtils";
import SessionDetailModal from "./SessionDetailModal";
import "./Communities.css";

const CalendarIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
    <rect x="3" y="5" width="18" height="16" rx="2" stroke="#2A987A" strokeWidth="1.5"/>
    <path d="M8 3v4M16 3v4" stroke="#2A987A" strokeWidth="1.5" strokeLinecap="round"/>
    <path d="M3 9h18" stroke="#2A987A" strokeWidth="1.5"/>
  </svg>
);

const GoogleCalendarIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
    <rect x="3" y="4" width="18" height="18" rx="2" stroke="currentColor" strokeWidth="1.5"/>
    <path d="M16 2v4M8 2v4M3 10h18" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
    <path d="M8 14l2 2 4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const IconClock = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="12" cy="12" r="10" />
    <polyline points="12 6 12 12 16 14" />
  </svg>
);

const IconVideo = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <polygon points="23 7 16 12 23 17 23 7" />
    <rect x="1" y="5" width="15" height="14" rx="2" ry="2" />
  </svg>
);

/* Countdown Timer Component */
function CountdownTimer({ nextEventDate, startTime, meetingLink }) {
  const [countdown, setCountdown] = useState(() =>
    calculateCountdown(nextEventDate, startTime)
  );

  useEffect(() => {
    const interval = setInterval(() => {
      setCountdown(calculateCountdown(nextEventDate, startTime));
    }, 60000); // Update every minute

    return () => clearInterval(interval);
  }, [nextEventDate, startTime]);

  if (!countdown) return null;

  if (countdown.isLive) {
    return (
      <a
        href={meetingLink}
        target="_blank"
        rel="noopener noreferrer"
        className="cop-btn join-meeting"
      >
        <IconVideo /> Join Meeting
      </a>
    );
  }

  return (
    <div className="cop-countdown">
      <IconClock />
      <span className="countdown-text">
        {countdown.days > 0 && `${countdown.days}d `}
        {countdown.hours}h {countdown.minutes}m until meeting
      </span>
    </div>
  );
}

export default function Communities() {
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [rsvpLoading, setRsvpLoading] = useState(null);

  const { data: eventsData, isLoading, error } = useGetPublicCommunityEventsQuery({});
  const { data: rsvpsData } = useGetMyRSVPsQuery();
  const [createRSVP] = useCreateRSVPMutation();

  const events = eventsData?.events || [];

  // Group events by parent to get all sessions for recurring events
  // Returns: { sessionsMap, uniqueEvents (one per community with next upcoming date) }
  const { sessionsMap, uniqueEvents } = useMemo(() => {
    const map = new Map();
    const now = new Date();

    events.forEach((event) => {
      // Skip events whose Eastern calendar day is fully over (keeps today's
      // events visible all day, including ones currently live).
      const dayEnd = getEventInstant(
        event.instanceDate || event.nextEventDate,
        "23:59"
      );
      if (dayEnd && dayEnd < now) return;

      // Determine the group key - use parent ID for instances, own ID for standalone
      let groupKey;
      if (event.eventType === 'instance' && event.parentEvent) {
        groupKey = String(event.parentEvent._id || event.parentEvent);
      } else {
        groupKey = String(event._id);
      }

      if (!map.has(groupKey)) {
        map.set(groupKey, []);
      }
      map.get(groupKey).push(event);
    });

    // Sort sessions by date within each group
    map.forEach((sessions) => {
      sessions.sort((a, b) => {
        const dateA = getEventInstant(a.instanceDate || a.nextEventDate, a.startTime);
        const dateB = getEventInstant(b.instanceDate || b.nextEventDate, b.startTime);
        return dateA - dateB;
      });
    });

    // Get unique events - one per group, using the first (next upcoming) session
    const unique = [];
    map.forEach((sessions) => {
      if (sessions.length > 0) {
        unique.push(sessions[0]); // First session is the next upcoming one
      }
    });

    // Sort unique events by next date
    unique.sort((a, b) => {
      const dateA = getEventInstant(a.instanceDate || a.nextEventDate, a.startTime);
      const dateB = getEventInstant(b.instanceDate || b.nextEventDate, b.startTime);
      return dateA - dateB;
    });

    return { sessionsMap: map, uniqueEvents: unique };
  }, [events]);

  // Helper to get all sessions for an event
  const getSessionsForEvent = (event) => {
    let groupKey;
    if (event.eventType === 'instance' && event.parentEvent) {
      groupKey = String(event.parentEvent._id || event.parentEvent);
    } else {
      groupKey = String(event._id);
    }
    return sessionsMap.get(groupKey) || [];
  };

  // Create a map of event IDs to RSVP status
  const rsvpStatusMap = useMemo(() => {
    const rsvps = rsvpsData || [];
    const statusMap = new Map();
    rsvps.forEach((rsvp) => {
      // Get event ID and convert to string for consistent comparison
      const eventId = String(rsvp.event?._id || rsvp.event);
      statusMap.set(eventId, rsvp.status || "pending");
    });
    return statusMap;
  }, [rsvpsData]);

  // Helper to get RSVP status for an event (check both event ID and parent ID)
  const getRSVPStatus = (event) => {
    const eventId = String(event._id || event.id || event);
    // Check for direct RSVP on this event
    let status = rsvpStatusMap.get(eventId);
    if (status) return status;

    // For instances, also check if there's an RSVP on the parent event
    if (event.parentEvent) {
      const parentId = String(event.parentEvent._id || event.parentEvent);
      status = rsvpStatusMap.get(parentId);
      if (status) return status;
    }

    return null;
  };

  const handleJoinRSVP = async (eventId) => {
    setRsvpLoading(eventId);
    try {
      await createRSVP(eventId).unwrap();
    } catch (err) {
      console.error("Failed to RSVP:", err);
    } finally {
      setRsvpLoading(null);
    }
  };

  const handleViewSession = (event) => {
    setSelectedEvent(event);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedEvent(null);
  };

  const handleModalRSVP = async () => {
    if (!selectedEvent) return;
    const eventId = selectedEvent._id;
    setRsvpLoading(eventId);
    try {
      await createRSVP(eventId).unwrap();
    } catch (err) {
      console.error("Failed to RSVP:", err);
    } finally {
      setRsvpLoading(null);
    }
  };

  if (isLoading) {
    return (
      <div className="cop-container">
        <button className="welcome" onClick={() => window.history.back()}>
          ← Back Home
        </button>
        <h1 className="username">Communities Of Practice</h1>
        <div className="cop-card">
          <p style={{ padding: "40px 20px", textAlign: "center", color: "#6b7280" }}>
            Loading events...
          </p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="cop-container">
        <button className="welcome" onClick={() => window.history.back()}>
          ← Back Home
        </button>
        <h1 className="username">Communities Of Practice</h1>
        <div className="cop-card">
          <p style={{ padding: "40px 20px", textAlign: "center", color: "#dc2626" }}>
            Error loading events. Please try again.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="cop-container">
      <button className="welcome" onClick={() => window.history.back()}>
        ← Back Home
      </button>
      <h1 className="username">Communities Of Practice</h1>

      {/* Hero */}
      <section className="cop-hero">
        <div className="cop-hero-inner">
          <h2>Transform values into action</h2>
          <p>
            Turn your organization's values into clear, practical competencies
            that guide hiring, promotion, and staff engagement and development.
          </p>
        </div>
      </section>

      <div className="cop-card">
        <div className="cop-card-head">Events</div>

        {uniqueEvents.length === 0 ? (
          <div style={{ padding: "40px 20px", textAlign: "center", color: "#6b7280" }}>
            No upcoming events at this time.
          </div>
        ) : (
          <div className="cop-grid">
            {uniqueEvents.map((ev) => {
              const eventId = ev._id || ev.id;
              const rsvpStatus = getRSVPStatus(ev);
              const isLoading = rsvpLoading === eventId;
              const isAccepted = rsvpStatus === "accepted";
              const isPending = rsvpStatus === "pending";

              const eventStatus = getEventStatus(
                ev.instanceDate || ev.nextEventDate,
                ev.startTime,
                ev.endTime
              );

              return (
                <div key={eventId} className="cop-event">
                  <div className="cop-event-top">
                    <div className="cop-event-title">{ev.title}</div>
                    <div className="cop-event-badges">
                      <span className={`event-status-badge status-${eventStatus.key}`}>
                        {eventStatus.key === "live" && <span className="status-dot" />}
                        {eventStatus.label}
                      </span>
                      <span className="cop-pill">{ev.cadence}</span>
                    </div>
                  </div>

                  <div className="cop-next">
                    <span className="cop-next-icon"><CalendarIcon /></span>
                    <span className="cop-next-text">
                      Next: {formatEventDateTime(ev.instanceDate || ev.nextEventDate, ev.startTime, ev.endTime)}
                    </span>
                  </div>

                  <div className="cop-actions">
                    {/* RSVP Button Logic */}
                    {isAccepted ? (
                      <CountdownTimer
                        nextEventDate={ev.instanceDate || ev.nextEventDate}
                        startTime={ev.startTime}
                        meetingLink={ev.meetingLink}
                      />
                    ) : isPending ? (
                      <button className="cop-btn pending" disabled>
                        Pending Approval
                      </button>
                    ) : (
                      <button
                        className="cop-btn primary"
                        onClick={() => handleJoinRSVP(eventId)}
                        disabled={isLoading}
                      >
                        {isLoading ? "Requesting..." : "RSVP →"}
                      </button>
                    )}

                    {/* Actions Row - View Session always visible, Calendar only for accepted */}
                    <div className="cop-actions-row">
                      <button
                        className="cop-btn ghost"
                        onClick={() => handleViewSession(ev)}
                      >
                        View Session →
                      </button>
                      {isAccepted && (
                        <a
                          href={generateGoogleCalendarUrl({
                            ...ev,
                            nextEventDate: ev.instanceDate || ev.nextEventDate
                          })}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="cop-btn calendar"
                          title="Add to Google Calendar"
                        >
                          <GoogleCalendarIcon /> Add to Calendar
                        </a>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Session Detail Modal */}
      {showModal && selectedEvent && (
        <SessionDetailModal
          event={selectedEvent}
          rsvpStatus={getRSVPStatus(selectedEvent)}
          onRSVP={handleModalRSVP}
          onClose={handleCloseModal}
          isLoading={rsvpLoading === selectedEvent._id}
          allSessions={getSessionsForEvent(selectedEvent)}
        />
      )}
    </div>
  );
}
